<template>
  <aside class="ms">
    <!-- Верхняя панель — быстрый доступ к активному действию -->
    <div class="ms__topbar">
      <div class="ms__badge" :class="{ 'is-active': !!modelValue }">
        <span v-if="modelValue" class="ms__badge-dot"></span>
        <span class="ms__badge-text">{{ activeToolLabel }}</span>
      </div>
      <button
        class="ms__btn ms__btn--ghost"
        :disabled="!modelValue"
        @click="$emit('update:modelValue', null); $emit('cancel')"
      >Отмена</button>
      <button
        v-if="modelValue && modelValue.startsWith('draw_') && modelValue !== 'draw_text'"
        class="ms__btn ms__btn--primary"
        @click="$emit('finish')"
      >Завершить линию</button>
    </div>

    <!-- Вкладки -->
    <div class="ms__tabs">
      <button
        v-for="t in tabs"
        :key="t.value"
        class="ms__tab"
        :class="{ 'is-active': tab === t.value }"
        @click="tab = t.value"
      >{{ t.label }}</button>
    </div>

    <!-- Вкладка «Инструменты» -->
    <div v-if="tab === 'tools'" class="ms__content">
      <!-- 1) Undo / Redo — заметная панель наверху -->
      <div class="ms__section ms__history">
        <button
          type="button"
          class="ms__history-btn"
          title="Отменить последнее действие (Ctrl+Z)"
          @click="$emit('undo')"
        >
          <span class="ms__history-icon">↶</span>
          <span>Отменить</span>
        </button>
        <button
          type="button"
          class="ms__history-btn"
          title="Повторить (Ctrl+Y)"
          @click="$emit('redo')"
        >
          <span class="ms__history-icon">↷</span>
          <span>Повторить</span>
        </button>
      </div>

      <!-- 2) Толщина линии -->
      <div class="ms__section">
        <div class="ms__section-title">Толщина линии и границы</div>
        <div class="ms__widths">
          <button
            v-for="w in strokeWidthPresets"
            :key="w"
            class="ms__width"
            :class="{ 'is-active': w === strokeWidth }"
            :title="`${w} px`"
            @click="strokeWidth = w; $emit('update:strokeWidth', w)"
          >
            <span class="ms__width-line" :style="{ height: w + 'px', background: color }"></span>
            <span class="ms__width-num">{{ w }}</span>
          </button>
        </div>
      </div>

      <!-- 3) Гибкие знаки — базовые пресеты -->
      <div class="ms__section">
        <div class="ms__section-title">Гибкие знаки (шаблоны линий)</div>
        <div class="ms__tools">
          <button
            v-for="it in markupTools"
            :key="it.key"
            class="ms__tool"
            :class="{ 'is-active': it.key === modelValue, 'ms__tool--eraser': it.key === 'draw_eraser' }"
            :title="it.label"
            @click="$emit('update:modelValue', it.key); $emit('select', it.key)"
          >
            <img v-if="it.src" :src="it.src" :alt="it.label" />
            <svg v-else class="ms__tool-preview" viewBox="0 0 60 24" preserveAspectRatio="xMidYMid meet">
              <g v-html="linePreviewSvg(it.preview || { mode: 'solid' }, color)"></g>
            </svg>
            <span class="ms__tool-label">{{ it.label }}</span>
          </button>
        </div>
      </div>

      <!-- 4) Полигоны / области -->
      <div class="ms__section">
        <div class="ms__section-title">Полигоны / области</div>
        <div class="ms__tools">
          <button
            v-for="it in polygonTools"
            :key="it.key"
            class="ms__tool"
            :class="{ 'is-active': it.key === modelValue }"
            :title="it.label"
            @click="$emit('update:modelValue', it.key); $emit('select', it.key)"
          >
            <img :src="it.src" :alt="it.label" />
            <span class="ms__tool-label">{{ it.label }}</span>
          </button>
        </div>
        <div class="ms__subsection-title">Граница области</div>
        <div class="ms__tools">
          <button
            v-for="d in areaDashes"
            :key="'dash-' + d.value"
            class="ms__tool"
            :class="{ 'is-active': d.value === areaDash }"
            :title="d.label"
            @click="$emit('update:areaDash', d.value)"
          >
            <svg class="ms__tool-preview" viewBox="0 0 60 24" preserveAspectRatio="xMidYMid meet">
              <g v-html="linePreviewSvg({ mode: d.value, weight: strokeWidth }, color)"></g>
            </svg>
            <span class="ms__tool-label">{{ d.label }}</span>
          </button>
        </div>
      </div>

      <!-- 5) Цвет линии — под полигонами -->
      <div class="ms__section">
        <div class="ms__section-title">Цвет линии и знаков</div>
        <div class="ms__colors">
          <button
            v-for="c in colors"
            :key="c.value"
            class="ms__swatch"
            :class="{ 'is-active': c.value === color }"
            :style="{ backgroundColor: c.value }"
            :title="c.label"
            @click="$emit('update:color', c.value)"
          ></button>
        </div>
      </div>

      <!-- 6) Цвет заливки — под цветом линии -->
      <div class="ms__section">
        <div class="ms__section-title">Цвет заливки полигонов</div>
        <div class="ms__colors">
          <button
            v-for="c in fillColors"
            :key="'fill-' + c.value"
            class="ms__swatch"
            :class="{ 'is-active': c.value === fillColor, 'is-transparent': c.value === 'transparent' }"
            :style="c.value === 'transparent' ? {} : { backgroundColor: c.value }"
            :title="c.label"
            @click="$emit('update:fillColor', c.value)"
          ></button>
        </div>
      </div>

      <!-- 7) Элементы для сборки гибких знаков: рисуем линию, ставим на неё элементы -->
      <div class="ms__section">
        <div class="ms__section-title ms__section-title--border">
          Элементы знаков
          <span class="ms__count">{{ lineElements.length }}</span>
        </div>
        <div class="ms__hint-inline">
          Нарисуйте линию, выберите элемент и наведите на линию — он прилипнет и
          развернётся вдоль неё. У конца линии встаёт в торец. Выделенный знак:
          размер и поворот — в панели внизу карты (R — поворот, +/− — размер).
        </div>
        <label class="ms__check">
          <input type="checkbox" :checked="snapToLine" @change="$emit('update:snapToLine', $event.target.checked)" />
          <span>Прилипать к линиям и границам</span>
        </label>
        <div v-if="snapToLine" class="ms__segmented">
          <button
            v-for="s in elementSides"
            :key="s.value"
            type="button"
            class="ms__segmented-btn"
            :class="{ 'is-active': s.value === elementSide }"
            @click="$emit('update:elementSide', s.value)"
          >{{ s.label }}</button>
        </div>
        <div class="ms__search ms__search--compact">
          <input
            v-model="elementSearch"
            class="ms__search-input"
            type="text"
            placeholder="Найти элемент…"
          />
        </div>
        <details
          v-for="g in filteredElementGroups"
          :key="g.key"
          class="ms__flex-group"
          :open="g.key === 'ends' || g.key === 'onLine' || !!elementSearch"
        >
          <summary class="ms__subsection-title">
            <span>{{ g.label }}</span>
            <span class="ms__count">{{ g.items.length }}</span>
          </summary>
          <div class="ms__elements">
            <button
              v-for="it in g.items"
              :key="it.key"
              type="button"
              class="ms__element"
              :class="{ 'is-active': 'lib.' + it.key === modelValue }"
              :title="it.label"
              @click="$emit('update:modelValue', 'lib.' + it.key); $emit('select', 'lib.' + it.key)"
            >
              <span class="ms__element-svg" v-html="elementPreview(it)"></span>
              <span class="ms__element-cap">{{ it.label }}</span>
            </button>
          </div>
        </details>
      </div>

    </div>

    <!-- Вкладка «Иконки» -->
    <div v-if="tab === 'icons'" class="ms__content">
      <div class="ms__search">
        <input
          v-model="iconSearch"
          class="ms__search-input"
          type="text"
          placeholder="Найти по названию…"
        />
      </div>

      <!-- Условные знаки ПС КНБ РК -->
      <template v-if="filteredPgs.length">
        <div class="ms__section-title">Условные знаки ПС КНБ РК</div>
        <div class="ms__icons">
          <button
            v-for="it in filteredPgs"
            :key="it.key"
            class="ms__icon ms__icon--pgs"
            :class="{ 'is-active': it.key === modelValue }"
            :title="it.label"
            @click="$emit('update:modelValue', it.key); $emit('select', it.key)"
          >
            <img :src="it.src" :alt="it.label" />
            <span class="ms__icon-cap">{{ it.label }}</span>
          </button>
        </div>
      </template>

      <!-- Полный набор пограничных знаков (из docx ПС КНБ РК) -->
      <template v-for="g in filteredBorderGroups" :key="g.category">
        <div class="ms__section-title ms__section-title--border">{{ g.label }} <span class="ms__count">{{ g.items.length }}</span></div>
        <div class="ms__icons">
          <button
            v-for="it in g.items"
            :key="it.key"
            class="ms__icon ms__icon--border"
            :class="{ 'is-active': it.key === modelValue }"
            :title="it.label"
            @click="$emit('update:modelValue', it.key); $emit('select', it.key)"
          >
            <img :src="it.src" :alt="it.label" />
            <span class="ms__icon-cap">{{ it.label }}</span>
          </button>
        </div>
      </template>

      <!-- Легаси-иконки (архив) — свёрнуты за деталью -->
      <details v-if="filteredLegacy.length" class="ms__legacy">
        <summary class="ms__section-title ms__section-title--muted">Легаси-набор (архив) · {{ filteredLegacy.length }}</summary>
        <div class="ms__icons">
          <button
            v-for="it in filteredLegacy"
            :key="it.key"
            class="ms__icon"
            :class="{ 'is-active': it.key === modelValue }"
            :title="it.label"
            @click="$emit('update:modelValue', it.key); $emit('select', it.key)"
          >
            <img :src="it.src" :alt="it.label" />
            <span class="ms__icon-cap">{{ it.label }}</span>
          </button>
        </div>
      </details>

      <div v-if="!filteredPgs.length && !filteredBorderGroups.length && !filteredLegacy.length && iconSearch" class="ms__empty">Ничего не найдено по «{{ iconSearch }}»</div>
      <div v-else-if="!filteredPgs.length && !filteredBorderGroups.length && !filteredLegacy.length" class="ms__empty">Локальные иконки не найдены</div>
    </div>

    <!-- Подсказки -->
    <div class="ms__hints">
      <div class="ms__hint">💡 Клик по иконке — выбор, клик по карте — установка</div>
      <div class="ms__hint">✏ Линии: клик за клик — точки маршрута</div>
      <div class="ms__hint">🗑 ПКМ по метке — удалить</div>
    </div>
  </aside>
</template>

<script>
// --- Условные знаки Пограничной службы КНБ РК ---
// SVG-набор специально для тактических карт заставы. Ключи (имена файлов)
// используются в API-сохранении меток, поэтому не переименовывать.
const PGS_LABELS = {
  '01_zastava.svg':      'Пограничная застава',
  '02_pgo.svg':          'ПГО (пограничное отделение)',
  '03_np.svg':           'Наблюдательный пункт (НП)',
  '04_sekret.svg':       'Секрет (С)',
  '05_chg.svg':          'Часовой границы (ЧГ)',
  '06_dozor.svg':        'Дозор (Д)',
  '07_pn.svg':           'Патрульный наряд (ПН)',
  '08_zasada.svg':       'Засада (Зс)',
  '09_tg.svg':           'Тревожная группа (ТГ)',
  '10_pg.svg':           'Поисковая группа (ПГ)',
  '11_stpn.svg':         'СТПН — старший наряда',
  '12_rlp.svg':          'Радиолокационный пост (РЛП)',
  '13_prozhektor.svg':   'Прожекторная станция',
  '14_tsog.svg':         'ТСОГ — датчик',
  '15_dog.svg':          'Служебная собака',
  '16_bpla.svg':         'БПЛА (беспилотник)',
  '17_engineer.svg':     'Инженерное заграждение',
  '18_mines.svg':        'Минное поле',
  '19_wire.svg':         'Проволочное заграждение',
  '20_stolb.svg':        'Пограничный столб',
  '21_taynik.svg':       'Тайник',
  '22_kp.svg':           'Командный пункт (КП)',
  '23_med.svg':          'Медицинский пункт',
  '24_arrow_dir.svg':    'Направление движения',
  '25_narushitel.svg':   'Нарушитель / противник',
  '26_otdel.svg':        'Отделение',
  '27_vzvod.svg':        'Взвод',
  '28_rota.svg':         'Рота',
  '29_post.svg':         'Пост охраны',
  '30_svyaz.svg':        'Пункт связи (ПС)',
  '31_okop.svg':         'Позиция / окоп',
  '32_bridge.svg':       'Мост',
  '33_shtab.svg':        'Штаб',
  '34_arm.svg':          'АРМ',
  '35_sanchast.svg':     'Санчасть',
}

const PGS_ICONS = (() => {
  try {
    const ctx = require.context('@/assets/images/map/pgs_icons', false, /\.svg$/i)
    return ctx.keys()
      .map((k) => {
        const src = ctx(k)
        const name = k.replace('./', '')
        return {
          key: `pgs_icons/${name}`, // префиксом отличаем от старых map_icons
          label: PGS_LABELS[name] || name.replace(/\.svg$/, ''),
          src,
          _pgs: true,
        }
      })
      .sort((a, b) => a.label.localeCompare(b.label, 'ru'))
  } catch (e) {
    console.warn('Не удалось загрузить pgs_icons:', e)
    return []
  }
})()

// Легаси-набор из map_icons (оставляем как fallback / архив)
const ALL_MAP_ICONS = (() => {
  try {
    const ctx = require.context('@/assets/images/map/map_icons', false, /\.(png|jpe?g|svg|bmp)$/i)
    const toLabel = (filename) => filename
      .replace(/\.[^.]+$/, '')
      .replace(/^img_\d+_?/, '')
      .replace(/[_-]+/g, ' ')
      .trim() || 'Иконка'
    return ctx.keys()
      .map((k) => {
        const src = ctx(k)
        const name = k.replace('./', '')
        return { key: name, label: toLabel(name), src }
      })
      .sort((a, b) => a.label.localeCompare(b.label, 'ru', { numeric: true }))
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Не удалось загрузить иконки из map_icons:', e)
    return []
  }
})()

// --- Полный набор пограничных знаков (из «Условные_тактические_знаки_набор_для_САКШЫ.docx») ---
// По ТЗ (встреча 27.08.2026, п.8) — 219 SVG сконвертированы из docx через LibreOffice.
// Каталог с подписями и категориями лежит рядом в border_signs.json
import BORDER_SIGNS_META from '@/assets/images/map/border_signs.json'
import { MAP_COLORS } from '@/components/maps/mapPalette'
import { ELEMENT_GROUPS, LINE_ELEMENTS, lineElementSvg } from '@/components/maps/lineElements'

const CATEGORY_LABELS = {
  dislocation:    'Места дислокации',
  crossing:       'Пункты пропуска',
  patrol:         'Пограничные наряды',
  observation:    'Наблюдение и техсредства',
  incident:       'Оперативная обстановка',
  aircraft:       'Летательные аппараты',
  vehicle:        'Наземная техника',
  weapon_comm:    'Оружие / связь',
  detection:      'Средства обнаружения',
  border_line:    'Границы / рубежи',
  customs:        'Таможня',
  crossing_infra: 'Инженерная инфраструктура',
  other_org:      'Взаимодействующие органы',
  operative:      'Оперативные лица / противник',
  facility:       'Учреждения',
  regulation:     'Регулирование движения',
  other:          'Прочее',
}
const CATEGORY_ORDER = [
  'dislocation','patrol','observation','border_line','incident','operative',
  'crossing','crossing_infra','customs','regulation',
  'aircraft','vehicle','weapon_comm','detection',
  'facility','other_org','other',
]

const BORDER_SIGNS = (() => {
  try {
    const ctx = require.context('@/assets/images/map/border_signs', false, /\.svg$/i)
    const byFile = {}
    ctx.keys().forEach((k) => { byFile[k.replace('./', '')] = ctx(k) })
    return BORDER_SIGNS_META
      .filter(m => byFile[m.file])
      .map(m => ({
        key: m.key,          // 'border_signs/N.svg'
        file: m.file,
        label: m.label || `Знак ${m.file.replace('.svg','')}`,
        category: m.category || 'other',
        flex: !!m.flex,
        src: byFile[m.file],
      }))
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Не удалось загрузить border_signs:', e)
    return []
  }
})()

// Гибкие знаки ПС (flex) целыми картинками больше не предлагаются — их собирают
// из элементов (lineElements.js). SVG-файлы остаются: ими нарисованы старые метки.
const BORDER_STATIC = BORDER_SIGNS.filter(s => !s.flex)

function groupBorderSigns(list, order = CATEGORY_ORDER) {
  const groups = {}
  list.forEach((it) => {
    (groups[it.category] = groups[it.category] || []).push(it)
  })
  return order
    .filter(cat => groups[cat] && groups[cat].length)
    .map(cat => ({
      category: cat,
      label: CATEGORY_LABELS[cat] || cat,
      items: groups[cat].sort((a, b) => a.label.localeCompare(b.label, 'ru')),
    }))
}

export default {
  name: 'IconsSidebar',
  props: {
    modelValue: { type: String, default: null },
    icons: { type: Array, default: null },
    color: { type: String, default: '#000000' },
    fillColor: { type: String, default: '#DC2626' },
    areaDash: { type: String, default: 'solid' },
    snapToLine: { type: Boolean, default: true },
    elementSide: { type: String, default: 'on' },
    fillPattern: { type: String, default: 'solid' },
  },
  emits: ['update:modelValue', 'update:color', 'update:fillColor', 'update:areaDash', 'update:snapToLine', 'update:elementSide', 'update:fillPattern', 'update:strokeWidth', 'select', 'cancel', 'finish', 'undo', 'redo'],

  data() {
    return {
      tab: 'tools',
      tabs: [
        { value: 'tools', label: 'Инструменты' },
        { value: 'icons', label: 'Иконки' },
      ],
      iconSearch: '',
      // ГИБКИЕ ЗНАКИ (шаблоны линий из docx «Условные_тактические_знаки_набор_для_САКШЫ»,
      // стр.37: «есть смысл сделать основной набор линий с выбором цвета и толщины».
      // Каждый шаблон — стиль рисования: пользователь тянет курсор → выходит линия
      // заданного вида. Цвет и толщина — общие настройки ниже.
      markupTools: [
        { key: 'draw_freehand',       label: 'Карандаш (свободно)',   preview: { mode: 'solid' } },
        { key: 'draw_solid',          label: 'Одинарная сплошная',    preview: { mode: 'solid' } },
        { key: 'draw_dashed',         label: 'Пунктир',               preview: { mode: 'dashed' } },
        { key: 'draw_dotted',         label: 'Точечный пунктир',      preview: { mode: 'dotted' } },
        { key: 'draw_dashdot',        label: 'Штрих-точка',           preview: { mode: 'dashdot' } },
        { key: 'draw_thick',          label: 'Толстая (тыльная граница)', preview: { mode: 'solid', weight: 6 } },
        { key: 'draw_double',         label: 'Двойная сплошная',      preview: { mode: 'double' } },
        { key: 'draw_double_dashed',  label: 'Двойной пунктир (рубеж)', preview: { mode: 'double_dashed' } },
        { key: 'draw_arrow',          label: 'Сплошная со стрелкой',  preview: { mode: 'arrow' } },
        { key: 'draw_arrow_double',   label: 'Двойная со стрелкой',   preview: { mode: 'arrow_double' } },
        { key: 'draw_ksp',            label: 'Контр.-следовая полоса', preview: { mode: 'ksp' } },
        { key: 'draw_band_solid',     label: 'Полоса сплошная',       preview: { mode: 'band' } },
        { key: 'draw_band_dashed',    label: 'Полоса пунктирная',     preview: { mode: 'band_dashed' } },
        { key: 'draw_band_dotted',    label: 'Полоса точечная',       preview: { mode: 'band_dotted' } },
        { key: 'draw_fence',          label: 'Заграждение',           preview: { mode: 'fence' } },
        { key: 'draw_text',           label: 'Текст',                 src: require('@/assets/images/map/tools/text.svg') },
        { key: 'draw_eraser',         label: 'Ластик',                src: require('@/assets/images/map/tools/eraser.svg') },
      ],
      strokeWidth: 3,
      strokeWidthPresets: [1, 2, 3, 5, 8],
      // ПОЛИГОНЫ / ОБЛАСТИ (по ТЗ, п.6 — новый тип: круги, квадраты, произвольные зоны)
      polygonTools: [
        { key: 'draw_polygon',   label: 'Произвольная область', src: require('@/assets/images/map/tools/polygon.svg') },
        { key: 'draw_polygon_free', label: 'Область от руки',  src: require('@/assets/images/map/tools/polygon.svg') },
        { key: 'draw_circle',    label: 'Круг',                src: require('@/assets/images/map/tools/polygon.svg') },
        { key: 'draw_rectangle', label: 'Прямоугольник',       src: require('@/assets/images/map/tools/polygon.svg') },
      ],
      // По встрече 03.09.2026: только штабные цвета. Основные — чёрный, красный, синий;
      // жёлтый, зелёный, коричневый — реже.
      colors: MAP_COLORS,
      // Заливка — те же цвета без альфы: прозрачность 35% задаёт карта (AREA_FILL_OPACITY).
      fillColors: [{ value: 'transparent', label: 'Без заливки' }, ...MAP_COLORS],
      lineElements: LINE_ELEMENTS,
      elementSearch: '',
      elementSides: [
        { value: 'on',    label: 'На линии' },
        { value: 'left',  label: 'Слева' },
        { value: 'right', label: 'Справа' },
      ],
      // Тип линии границы областей (полигон / круг / прямоугольник); толщина — общая.
      areaDashes: [
        { value: 'solid',   label: 'Сплошная' },
        { value: 'dashed',  label: 'Пунктир' },
        { value: 'dotted',  label: 'Точечный' },
        { value: 'dashdot', label: 'Штрих-точка' },
      ],
    }
  },
  computed: {
    iconList() {
      if (Array.isArray(this.icons) && this.icons.length) return this.icons
      return [...PGS_ICONS, ...BORDER_SIGNS, ...ALL_MAP_ICONS]
    },
    pgsIcons() { return PGS_ICONS },
    legacyIcons() { return ALL_MAP_ICONS },
    borderStaticSigns() { return BORDER_STATIC },
    filteredPgs() {
      const q = this.iconSearch.trim().toLowerCase()
      if (!q) return this.pgsIcons
      return this.pgsIcons.filter(it =>
        it.label.toLowerCase().includes(q) || it.key.toLowerCase().includes(q)
      )
    },
    filteredLegacy() {
      const q = this.iconSearch.trim().toLowerCase()
      if (!q) return this.legacyIcons
      return this.legacyIcons.filter(it =>
        it.label.toLowerCase().includes(q) || it.key.toLowerCase().includes(q)
      )
    },
    filteredBorderGroups() {
      const q = this.iconSearch.trim().toLowerCase()
      const filtered = q
        ? this.borderStaticSigns.filter(it =>
            it.label.toLowerCase().includes(q) || it.key.toLowerCase().includes(q))
        : this.borderStaticSigns
      return groupBorderSigns(filtered, CATEGORY_ORDER)
    },
    filteredElementGroups() {
      const q = this.elementSearch.trim().toLowerCase()
      const items = q
        ? LINE_ELEMENTS.filter(it => it.label.toLowerCase().includes(q) || it.key.includes(q))
        : LINE_ELEMENTS
      return ELEMENT_GROUPS
        .map(grp => ({ ...grp, items: items.filter(it => it.group === grp.key) }))
        .filter(grp => grp.items.length)
    },
    activeToolLabel() {
      if (!this.modelValue) return 'Инструмент не выбран'
      if (this.modelValue.startsWith('lib.')) {
        const el = LINE_ELEMENTS.find(it => 'lib.' + it.key === this.modelValue)
        if (el) return el.label
      }
      const mt = this.markupTools.find(t => t.key === this.modelValue)
      if (mt) return mt.label
      const ic = this.iconList.find(i => i.key === this.modelValue)
      return ic ? ic.label : 'Активная иконка'
    },
  },
  methods: {
    elementPreview(it) {
      return lineElementSvg(it, { color: this.color })
    },
    // Рисует SVG-фрагмент, соответствующий заданному стилю линии.
    // Стили соответствуют документу «Условные_тактические_знаки_набор_для_САКШЫ.docx» (стр. 37).
    linePreviewSvg(preview, color) {
      const c = color || '#111'
      const w = preview.weight || 3
      const mode = preview.mode
      const y = 12
      switch (mode) {
        case 'dashed':
          return `<line x1="4" y1="${y}" x2="56" y2="${y}" stroke="${c}" stroke-width="${w}" stroke-dasharray="6 4" stroke-linecap="butt"/>`
        case 'dotted':
          return `<line x1="4" y1="${y}" x2="56" y2="${y}" stroke="${c}" stroke-width="${w}" stroke-dasharray="1 4" stroke-linecap="round"/>`
        case 'dashdot':
          return `<line x1="4" y1="${y}" x2="56" y2="${y}" stroke="${c}" stroke-width="${w}" stroke-dasharray="7 3 1 3" stroke-linecap="butt"/>`
        case 'double':
          return `<line x1="4" y1="${y-3}" x2="56" y2="${y-3}" stroke="${c}" stroke-width="${w}"/>` +
                 `<line x1="4" y1="${y+3}" x2="56" y2="${y+3}" stroke="${c}" stroke-width="${w}"/>`
        case 'double_dashed':
          return `<line x1="4" y1="${y-3}" x2="56" y2="${y-3}" stroke="${c}" stroke-width="${w}" stroke-dasharray="6 3"/>` +
                 `<line x1="4" y1="${y+3}" x2="56" y2="${y+3}" stroke="${c}" stroke-width="${w}" stroke-dasharray="6 3"/>`
        case 'arrow':
          return `<line x1="4" y1="${y}" x2="48" y2="${y}" stroke="${c}" stroke-width="${w}"/>` +
                 `<polygon points="48,${y-5} 56,${y} 48,${y+5}" fill="${c}"/>`
        case 'arrow_double':
          return `<line x1="4" y1="${y-3}" x2="48" y2="${y-3}" stroke="${c}" stroke-width="${w}"/>` +
                 `<line x1="4" y1="${y+3}" x2="48" y2="${y+3}" stroke="${c}" stroke-width="${w}"/>` +
                 `<polygon points="48,${y-6} 56,${y} 48,${y+6}" fill="${c}"/>`
        case 'ksp': {
          // По ТЗ 03.09.2026: КСП = двойная сплошная (края полосы) +
          // диагональная штриховка внутри. Совпадает с pattern-рендером на карте.
          const yTop = y - 5, yBot = y + 5
          const patId = `kspPrev-${Math.abs((c || '').split('').reduce((a,ch)=>a+ch.charCodeAt(0),0))}`
          return `<defs><pattern id="${patId}" width="8" height="10" patternUnits="userSpaceOnUse">` +
                 `<line x1="0" y1="0" x2="8" y2="10" stroke="${c}" stroke-width="1"/></pattern></defs>` +
                 `<rect x="4" y="${yTop}" width="52" height="10" fill="url(#${patId})"/>` +
                 `<line x1="4" y1="${yTop}" x2="56" y2="${yTop}" stroke="${c}" stroke-width="1.5"/>` +
                 `<line x1="4" y1="${yBot}" x2="56" y2="${yBot}" stroke="${c}" stroke-width="1.5"/>`
        }
        case 'band':
          return `<line x1="4" y1="${y}" x2="56" y2="${y}" stroke="${c}" stroke-width="10" stroke-linecap="butt"/>`
        case 'band_dashed':
          return `<line x1="4" y1="${y}" x2="56" y2="${y}" stroke="${c}" stroke-width="10" ` +
                 `stroke-dasharray="14 6" stroke-linecap="butt"/>`
        case 'band_dotted':
          return `<line x1="4" y1="${y}" x2="56" y2="${y}" stroke="${c}" stroke-width="10" ` +
                 `stroke-dasharray="2 8" stroke-linecap="round"/>`
        case 'fence':
          return `<line x1="4" y1="${y}" x2="56" y2="${y}" stroke="${c}" stroke-width="${w}"/>` +
                 [8, 20, 32, 44].map(x =>
                   `<line x1="${x}" y1="${y-4}" x2="${x}" y2="${y+4}" stroke="${c}" stroke-width="1.5"/>`
                 ).join('')
        case 'solid':
        default:
          return `<line x1="4" y1="${y}" x2="56" y2="${y}" stroke="${c}" stroke-width="${w}" stroke-linecap="round"/>`
      }
    },
  },
}
</script>

<style scoped>
.ms {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 14px;
  overflow: hidden;
  flex: 0 0 340px;
  width: 340px;
  max-width: 340px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
}

/* Верхняя панель */
.ms__topbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #F9FAFB;
  border-bottom: 1px solid #E5E7EB;
}
.ms__badge {
  flex: 1 1 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #F3F4F6;
  border-radius: 999px;
  color: #6B7280;
  font-size: 13px;
  font-weight: 600;
  min-width: 0;
}
.ms__badge.is-active { background: #EFF6FF; color: #1D4ED8; }
.ms__badge-dot { width: 8px; height: 8px; border-radius: 50%; background: #2563EB; animation: pulse 1.5s ease-in-out infinite; }
@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
.ms__badge-text { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.ms__btn {
  height: 32px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: background 0.15s, border-color 0.15s;
}
.ms__btn:disabled { opacity: 0.4; cursor: not-allowed; }
.ms__btn--ghost { background: #fff; border-color: #D1D5DB; color: #374151; }
.ms__btn--ghost:hover:not(:disabled) { background: #F3F4F6; }
.ms__btn--primary { background: #2F78EE; color: #fff; }
.ms__btn--primary:hover:not(:disabled) { background: #1D4ED8; }

/* Вкладки */
.ms__tabs { display: flex; padding: 0 12px; background: #fff; border-bottom: 1px solid #E5E7EB; }
.ms__tab {
  flex: 1 1 0;
  padding: 12px 0;
  background: transparent;
  border: 0;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #6B7280;
  transition: color 0.15s, border-color 0.15s;
}
.ms__tab:hover { color: #374151; }
.ms__tab.is-active { color: #1D4ED8; border-bottom-color: #2F78EE; }

/* Контент вкладки */
.ms__content { flex: 1 1 auto; overflow-y: auto; padding: 16px 12px; display: flex; flex-direction: column; gap: 16px; }
.ms__section { display: flex; flex-direction: column; gap: 8px; }
.ms__section-title {
  font-size: 12px;
  font-weight: 800;
  color: #1F2937;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin: 8px 0 4px;
  padding: 6px 8px;
  background: #EFF6FF;
  border-left: 4px solid #2F78EE;
  border-radius: 4px;
}
.ms__section-title--muted {
  color: #6B7280;
  background: #F3F4F6;
  border-left-color: #9CA3AF;
  margin-top: 16px;
}
.ms__icon--pgs { background: #FFFFFF; }
.ms__icon--pgs:hover { border-color: #DC2626; background: #FEF2F2; }
.ms__icon--pgs.is-active { border-color: #DC2626; background: #FEE2E2; box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.15); }
.ms__icon--pgs img { max-width: 70%; max-height: 70%; }

.ms__section-title--border {
  background: #ECFEFF;
  border-left-color: #0891B2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
}
.ms__count {
  font-weight: 700;
  font-size: 11px;
  color: #0891B2;
  background: #CFFAFE;
  padding: 1px 6px;
  border-radius: 8px;
}
.ms__icon--border { background: #FFFFFF; }
.ms__icon--border:hover { border-color: #0891B2; background: #ECFEFF; }
.ms__icon--border.is-active { border-color: #0891B2; background: #CFFAFE; box-shadow: 0 0 0 2px rgba(8, 145, 178, 0.15); }
.ms__icon--border img { max-width: 78%; max-height: 78%; }


/* Undo/Redo — две кнопки в одной строке */
.ms__history {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  padding-top: 8px;
}
.ms__history-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 6px;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  background: #F9FAFB;
  color: #111827;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background .15s, border-color .15s;
}
.ms__history-btn:hover {
  background: #EFF6FF;
  border-color: #2F78EE;
  color: #2F78EE;
}
.ms__history-icon {
  font-size: 18px;
  line-height: 1;
}

/* Раскрывающиеся группы гибких знаков ПС */
/* Элементы знаков (библиотека для сборки гибких знаков) */
.ms__check {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #374151;
  padding: 2px 4px 6px;
  cursor: pointer;
}
.ms__check input { accent-color: #2F78EE; }
.ms__segmented {
  display: flex;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  overflow: hidden;
  margin: 0 4px 4px;
}
.ms__segmented-btn {
  flex: 1;
  padding: 5px 4px;
  border: none;
  background: #F9FAFB;
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  font-family: inherit;
}
.ms__segmented-btn + .ms__segmented-btn { border-left: 1px solid #E5E7EB; }
.ms__segmented-btn.is-active { background: #EFF6FF; color: #1D4ED8; }
.ms__elements {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  margin-top: 6px;
}
.ms__element {
  display: grid;
  grid-template-rows: 34px auto;
  gap: 2px;
  padding: 5px 3px 4px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  font-family: inherit;
  min-width: 0;
}
.ms__element:hover { border-color: #2F78EE; background: #F8FAFF; }
.ms__element.is-active { border-color: #2F78EE; background: #EFF6FF; box-shadow: 0 0 0 2px rgba(47, 120, 238, 0.15); }
.ms__element-svg { display: flex; align-items: center; justify-content: center; height: 34px; }
.ms__element-svg :deep(svg) { width: 30px !important; height: 30px !important; }
.ms__element-cap {
  font-size: 9px;
  line-height: 1.15;
  color: #4B5563;
  text-align: center;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.ms__flex-group { margin-top: 8px; }
.ms__flex-group > summary {
  cursor: pointer;
  user-select: none;
  list-style: revert;
}

/* Ластик — выделяем красным */
.ms__tool--eraser {
  border-color: #FCA5A5 !important;
  background: #FEF2F2 !important;
}
.ms__tool--eraser:hover { background: #FEE2E2 !important; }
.ms__tool--eraser.is-active {
  background: #FCA5A5 !important;
  border-color: #DC2626 !important;
  box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.2);
}

.ms__hint-inline {
  padding: 6px 10px;
  background: #FEF3C7;
  color: #78350F;
  font-size: 11px;
  border-radius: 6px;
  margin: 6px 0 8px;
  line-height: 1.3;
}

.ms__subsection-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #64748B;
  letter-spacing: 0.4px;
  padding: 6px 4px 4px;
  margin-top: 6px;
  border-bottom: 1px dashed #E5E7EB;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.ms__search--compact { padding: 6px 8px 4px; }
.ms__search--compact .ms__search-input {
  font-size: 12px;
  padding: 6px 8px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  width: 100%;
  outline: none;
}

.ms__legacy { margin-top: 12px; }
.ms__legacy > summary {
  cursor: pointer;
  list-style: revert;
}

.ms__tools {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}
.ms__tool {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 10px 6px;
  border-radius: 10px;
  border: 1px solid #E5E7EB;
  background: #F9FAFB;
  cursor: pointer;
  font-family: inherit;
  transition: border-color 0.15s, background 0.15s;
}
.ms__tool:hover { border-color: #A5B4FC; background: #F5F3FF; }
.ms__tool.is-active { border-color: #2F78EE; background: #EFF6FF; box-shadow: 0 0 0 2px rgba(47, 120, 238, 0.15); }
.ms__tool img { width: 24px; height: 24px; }
.ms__tool-preview { width: 100%; height: 22px; }

/* Слайдер толщины линии */
.ms__widths {
  display: flex;
  gap: 6px;
  align-items: stretch;
}
.ms__width {
  flex: 1;
  padding: 8px 4px;
  border: 1px solid #E5E7EB;
  background: #FFFFFF;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-height: 42px;
  justify-content: center;
  transition: border-color .15s, background .15s;
}
.ms__width:hover { border-color: #2F78EE; background: #F5F8FF; }
.ms__width.is-active {
  border-color: #2F78EE;
  background: #EFF6FF;
  box-shadow: 0 0 0 2px rgba(47,120,238,.15);
}
.ms__width-line {
  display: block;
  width: 80%;
  min-height: 1px;
  border-radius: 2px;
  background: #111827;
}
.ms__width-num {
  font-size: 10px;
  color: #6B7280;
  font-weight: 600;
}

/* КСП — теперь превью рисуется inline-SVG, специальный фон не нужен */
.ms__tool-label { font-size: 11px; color: #4B5563; text-align: center; line-height: 1.2; }

.ms__colors { display: flex; flex-wrap: wrap; gap: 10px; }
.ms__swatch {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #E5E7EB;
  cursor: pointer;
  padding: 0;
  position: relative;
  transition: transform 0.1s, box-shadow 0.15s;
}
.ms__swatch:hover { transform: scale(1.1); }
.ms__swatch.is-active {
  border-color: #2F78EE;
  transform: scale(1.15);
  box-shadow: 0 0 0 3px rgba(47, 120, 238, 0.2);
}
.ms__swatch.is-transparent {
  background:
    linear-gradient(45deg, #E5E7EB 25%, transparent 25%, transparent 75%, #E5E7EB 75%),
    linear-gradient(45deg, #E5E7EB 25%, #fff 25%, #fff 75%, #E5E7EB 75%);
  background-size: 8px 8px;
  background-position: 0 0, 4px 4px;
}

/* Поиск иконок */
.ms__search { display: flex; align-items: center; padding: 0 4px; }
.ms__search-input {
  width: 100%;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font-size: 13px;
  color: #111827;
  background: #F9FAFB;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s, background 0.15s;
}
.ms__search-input:focus { border-color: #2F78EE; background: #fff; box-shadow: 0 0 0 2px rgba(47, 120, 238, 0.15); }

/* Сетка иконок */
.ms__icons {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
}
.ms__icon {
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px;
  border-radius: 8px;
  border: 1px solid #E5E7EB;
  background: #F9FAFB;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  overflow: hidden;
  position: relative;
}
.ms__icon:hover { border-color: #A5B4FC; background: #F5F3FF; }
.ms__icon.is-active { border-color: #2F78EE; background: #EFF6FF; box-shadow: 0 0 0 2px rgba(47, 120, 238, 0.15); }
.ms__icon img {
  display: block;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  object-position: center;
  min-height: 0;
}
/* Оставляем место под подпись — картинка занимает верхние 62% кнопки */
.ms__icon { display: grid; grid-template-rows: 1fr auto; }
.ms__icon-cap { align-self: end; }
.ms__icon-cap {
  font-size: 9px;
  color: #6B7280;
  text-align: center;
  line-height: 1.1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.ms__empty { padding: 24px 12px; text-align: center; color: #9CA3AF; font-size: 13px; }

/* Подсказки */
.ms__hints {
  border-top: 1px solid #E5E7EB;
  padding: 10px 12px;
  background: #FAFAFA;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.ms__hint { font-size: 14px; color: #374151; line-height: 1.55; font-weight: 500; }
</style>
